import { useState, useEffect } from "react";
import { X, Check, Image, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImageLibraryPicker({ open, onClose, onSelect, multiple = false }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (open) { fetchImages(); setSelected([]); }
  }, [open]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/media-library`, { headers: { Authorization: `Bearer ${token}` } });
      setImages(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const toggleSelect = (url) => {
    if (multiple) {
      setSelected(prev => prev.includes(url) ? prev.filter(u => u !== url) : [...prev, url]);
    } else {
      setSelected(prev => prev.includes(url) ? [] : [url]);
    }
  };

  const handleConfirm = () => {
    if (selected.length === 0) return;
    onSelect(multiple ? selected : selected[0]);
    onClose();
  };

  if (!open) return null;

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', pointerEvents: 'auto' }}
      data-testid="image-library-picker"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{ background: '#fff', width: '100%', maxWidth: '800px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', margin: '16px', pointerEvents: 'auto', position: 'relative' }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', borderBottom: '1px solid #e5e0d8' }}>
          <div>
            <h3 style={{ fontSize: '18px', color: '#6B5B4F', margin: 0 }}>Image Library</h3>
            <p style={{ fontSize: '12px', color: '#6B5B4F80', margin: '4px 0 0' }}>{multiple ? "Click images to select, then confirm" : "Click an image to select it"}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#6B5B4F80' }}>
            <X size={20} />
          </button>
        </div>

        {/* Image Grid */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0' }}>
              <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#9A7B4F' }} />
            </div>
          ) : images.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '64px 0' }}>
              <Image size={48} style={{ color: '#6B5B4F33', margin: '0 auto 12px' }} />
              <p style={{ color: '#6B5B4F80', fontSize: '14px' }}>No images uploaded yet.</p>
              <p style={{ color: '#6B5B4F4D', fontSize: '12px', marginTop: '4px' }}>Upload images via Batch Upload first.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '12px' }}>
              {images.map((img) => {
                const isSelected = selected.includes(img.url);
                return (
                  <div
                    key={img.id}
                    onClick={() => toggleSelect(img.url)}
                    style={{
                      position: 'relative',
                      aspectRatio: '1',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: isSelected ? '3px solid #9A7B4F' : '2px solid transparent',
                      boxShadow: isSelected ? '0 0 0 3px rgba(154,123,79,0.3)' : 'none',
                      transition: 'all 0.15s',
                    }}
                    data-testid={`library-image-${img.id}`}
                  >
                    <img
                      src={img.url}
                      alt={img.original_name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
                    />
                    {isSelected && (
                      <div style={{ position: 'absolute', inset: 0, background: 'rgba(154,123,79,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#9A7B4F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Check size={16} color="#fff" />
                        </div>
                      </div>
                    )}
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.5))', padding: '6px', pointerEvents: 'none' }}>
                      <p style={{ color: '#fff', fontSize: '9px', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{img.original_name}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderTop: '1px solid #e5e0d8', background: '#FAF7F2' }}>
          <span style={{ fontSize: '13px', color: '#6B5B4F80' }}>{selected.length} selected</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="outline" onClick={onClose} className="border-[#282828]/20 text-[#676664]/60">Cancel</Button>
            <Button
              onClick={handleConfirm}
              disabled={selected.length === 0}
              className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white disabled:opacity-40"
              data-testid="library-confirm-btn"
            >
              {multiple ? `Add ${selected.length} Image${selected.length !== 1 ? 's' : ''}` : "Select Image"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
